import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Plus,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
  XCircle,
} from 'lucide-react';
import adminApi from '../utils/adminApi';
import { useAdminAuth } from '../utils/AdminAuthContext';
import { buildPublicBookingUrl } from '../utils/booking';

const DAY_LABELS = ['Niedz', 'Pon', 'Wt', 'Sr', 'Czw', 'Pt', 'Sob'];

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('pl-PL');
}

function formatMoney(value) {
  if (value == null || value === '') return '-';
  return `${Number(value).toFixed(2)} zl`;
}

function formatActionLabel(action) {
  const labels = {
    professional_updated: 'Zapisano dane konta',
    password_reset_requested: 'Wyslano reset hasla',
    booking_slug_generated: 'Wygenerowano slug',
    professional_auto_activated: 'Automatycznie aktywowano konto',
    admin_service_created: 'Dodano usluge',
    admin_service_updated: 'Zaktualizowano usluge',
    admin_service_deactivated: 'Wylaczono usluge',
    admin_schedule_updated: 'Zmieniono grafik',
    admin_block_created: 'Dodano blokade',
    admin_block_deleted: 'Usunieto blokade',
    admin_request_accepted: 'Zaakceptowano prosbe',
    admin_request_rejected: 'Odrzucono prosbe',
  };

  return labels[action] || action;
}

function createEmptyAddon() {
  return {
    id: null,
    name: '',
    duration_minutes: 10,
    price: 0,
    is_active: true,
  };
}

function createEmptyService() {
  return {
    name: '',
    description: '',
    duration_minutes: 60,
    price: 0,
    promo_price: '',
    promo_label: '',
    addons: [],
  };
}

function createScheduleRow() {
  return {
    local_id: `schedule-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    day_of_week: 1,
    start_time: '09:00:00',
    end_time: '17:00:00',
    is_active: true,
  };
}

function createBlockDraft() {
  return {
    reason: 'Przerwa',
    block_type: 'break',
    blocked_from: '',
    blocked_until: '',
  };
}

function normalizeTimeValue(value) {
  if (!value) return '09:00';
  return String(value).slice(0, 5);
}

function ensureTimeSeconds(value) {
  if (!value) return '09:00:00';
  return value.length === 5 ? `${value}:00` : value;
}

function normalizeProfessionalPayload(data) {
  return {
    ...data,
    services: Array.isArray(data?.services)
      ? data.services.map((service) => ({
          ...service,
          promo_price: service.promo_price ?? '',
          promo_label: service.promo_label || '',
          description: service.description || '',
          addons: Array.isArray(service.addons) ? service.addons : [],
        }))
      : [],
    availability: Array.isArray(data?.availability)
      ? data.availability.map((slot) => ({
          ...slot,
          start_time: ensureTimeSeconds(slot.start_time),
          end_time: ensureTimeSeconds(slot.end_time),
        }))
      : [],
    time_blocks: Array.isArray(data?.time_blocks) ? data.time_blocks : [],
    requests: Array.isArray(data?.requests) ? data.requests : [],
    appointments: Array.isArray(data?.appointments) ? data.appointments : [],
    clients: Array.isArray(data?.clients) ? data.clients : [],
    admin_actions: Array.isArray(data?.admin_actions) ? data.admin_actions : [],
  };
}

export default function AdminDashboardPage() {
  const { admin, logout } = useAdminAuth();
  const [dashboard, setDashboard] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [serviceDraft, setServiceDraft] = useState(createEmptyService());
  const [blockDraft, setBlockDraft] = useState(createBlockDraft());

  async function loadOverview(searchValue = '') {
    const [dashboardResponse, professionalsResponse] = await Promise.all([
      adminApi.get('/admin/dashboard'),
      adminApi.get(`/admin/professionals${searchValue.trim() ? `?search=${encodeURIComponent(searchValue.trim())}` : ''}`),
    ]);

    setDashboard(dashboardResponse.data);
    setProfessionals(Array.isArray(professionalsResponse.data) ? professionalsResponse.data : []);
  }

  async function loadDetails(professionalId) {
    if (!professionalId) {
      setSelectedProfessional(null);
      return;
    }

    setDetailLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/professionals/${professionalId}`);
      setSelectedProfessional(normalizeProfessionalPayload(data));
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udalo sie pobrac szczegolow konta.');
    } finally {
      setDetailLoading(false);
    }
  }

  async function refreshSelection(professionalId = selectedId, nextSearch = search) {
    await Promise.all([loadOverview(nextSearch), loadDetails(professionalId)]);
  }

  useEffect(() => {
    async function loadInitial() {
      setLoading(true);
      try {
        await loadOverview();
      } catch (err) {
        setError(err.response?.data?.error || 'Nie udalo sie pobrac danych admina.');
      } finally {
        setLoading(false);
      }
    }

    loadInitial();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setSelectedProfessional(null);
      return;
    }

    loadDetails(selectedId);
  }, [selectedId]);

  function updateSelectedProfessional(updater) {
    setSelectedProfessional((current) => {
      if (!current) return current;
      return updater(current);
    });
  }

  async function handleSearchSubmit(event) {
    event.preventDefault();
    setError('');
    setSaveMessage('');
    await loadOverview(search);
  }

  async function handleSaveProfile() {
    if (!selectedProfessional?.professional) return;

    setActionLoading(true);
    setError('');
    setSaveMessage('');

    const payload = {
      account_status: selectedProfessional.professional.account_status,
      admin_notes: selectedProfessional.professional.admin_notes,
    };

    try {
      await adminApi.patch(`/admin/professionals/${selectedProfessional.professional.id}`, payload);
      await refreshSelection(selectedProfessional.professional.id);
      setSaveMessage('Zmiany zapisane.');
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udalo sie zapisac zmian.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSendResetLink() {
    if (!selectedProfessional?.professional) return;

    setActionLoading(true);
    setError('');
    setSaveMessage('');

    try {
      const { data } = await adminApi.post(`/admin/professionals/${selectedProfessional.professional.id}/reset-password`);
      setSaveMessage(data.delivery?.delivered
        ? 'Link resetujacy zostal wyslany.'
        : 'Link resetujacy zostal wygenerowany. Backend uzyl fallbacku bez dostawcy email.');
      await refreshSelection(selectedProfessional.professional.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udalo sie wyslac resetu hasla.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleGenerateBookingSlug() {
    if (!selectedProfessional?.professional) return;

    setActionLoading(true);
    setError('');
    setSaveMessage('');

    try {
      await adminApi.post(`/admin/professionals/${selectedProfessional.professional.id}/generate-booking-slug`);
      await refreshSelection(selectedProfessional.professional.id);
      setSaveMessage('Slug rezerwacji zostal wygenerowany.');
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udalo sie wygenerowac slugu.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleActivateReady() {
    if (!selectedProfessional?.professional) return;

    setActionLoading(true);
    setError('');
    setSaveMessage('');

    try {
      await adminApi.post(`/admin/professionals/${selectedProfessional.professional.id}/activate-ready`);
      await refreshSelection(selectedProfessional.professional.id);
      setSaveMessage('Konto zostalo aktywowane automatycznie.');
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.summary) {
        updateSelectedProfessional((prev) => ({
          ...prev,
          summary: err.response.data.summary,
        }));
      }
      setError(err.response?.data?.error || 'Nie udalo sie aktywowac konta.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleCopyBookingLink() {
    if (!selectedProfessional?.professional?.booking_slug) {
      setError('To konto nie ma jeszcze slugu rezerwacji do skopiowania.');
      return;
    }

    try {
      await navigator.clipboard.writeText(buildPublicBookingUrl(selectedProfessional.professional.booking_slug));
      setSaveMessage('Link do rezerwacji zostal skopiowany.');
    } catch {
      setError('Nie udalo sie skopiowac linku.');
    }
  }

  function updateServiceDraft(field, value) {
    setServiceDraft((current) => ({ ...current, [field]: value }));
  }

  function updateServiceDraftAddon(index, field, value) {
    setServiceDraft((current) => ({
      ...current,
      addons: current.addons.map((addon, addonIndex) => (addonIndex === index ? { ...addon, [field]: value } : addon)),
    }));
  }

  function addServiceDraftAddon() {
    setServiceDraft((current) => ({ ...current, addons: [...current.addons, createEmptyAddon()] }));
  }

  function removeServiceDraftAddon(index) {
    setServiceDraft((current) => ({
      ...current,
      addons: current.addons.filter((_, addonIndex) => addonIndex !== index),
    }));
  }

  async function handleCreateService() {
    if (!selectedProfessional?.professional) return;

    setActionLoading(true);
    setError('');
    setSaveMessage('');

    try {
      await adminApi.post(`/admin/professionals/${selectedProfessional.professional.id}/services`, serviceDraft);
      setServiceDraft(createEmptyService());
      await refreshSelection(selectedProfessional.professional.id);
      setSaveMessage('Usluga zostala dodana.');
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udalo sie dodac uslugi.');
    } finally {
      setActionLoading(false);
    }
  }

  function updateExistingService(serviceId, field, value) {
    updateSelectedProfessional((prev) => ({
      ...prev,
      services: prev.services.map((service) => (service.id === serviceId ? { ...service, [field]: value } : service)),
    }));
  }

  function addExistingServiceAddon(serviceId) {
    updateSelectedProfessional((prev) => ({
      ...prev,
      services: prev.services.map((service) => (
        service.id === serviceId
          ? { ...service, addons: [...(service.addons || []), createEmptyAddon()] }
          : service
      )),
    }));
  }

  function updateExistingServiceAddon(serviceId, addonIndex, field, value) {
    updateSelectedProfessional((prev) => ({
      ...prev,
      services: prev.services.map((service) => (
        service.id === serviceId
          ? {
              ...service,
              addons: (service.addons || []).map((addon, index) => (index === addonIndex ? { ...addon, [field]: value } : addon)),
            }
          : service
      )),
    }));
  }

  function removeExistingServiceAddon(serviceId, addonIndex) {
    updateSelectedProfessional((prev) => ({
      ...prev,
      services: prev.services.map((service) => (
        service.id === serviceId
          ? { ...service, addons: (service.addons || []).filter((_, index) => index !== addonIndex) }
          : service
      )),
    }));
  }

  async function handleSaveService(service) {
    setActionLoading(true);
    setError('');
    setSaveMessage('');

    try {
      await adminApi.patch(`/admin/services/${service.id}`, {
        name: service.name,
        description: service.description,
        duration_minutes: Number(service.duration_minutes),
        price: Number(service.price),
        promo_price: service.promo_price === '' ? '' : Number(service.promo_price),
        promo_label: service.promo_label,
        is_active: service.is_active !== false,
        addons: (service.addons || []).map((addon) => ({
          id: addon.id,
          name: addon.name,
          duration_minutes: Number(addon.duration_minutes),
          price: Number(addon.price),
          is_active: addon.is_active !== false,
        })),
      });
      await refreshSelection(selectedProfessional.professional.id);
      setSaveMessage(`Usluga "${service.name}" zostala zapisana.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udalo sie zapisac uslugi.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDeactivateService(serviceId) {
    setActionLoading(true);
    setError('');
    setSaveMessage('');

    try {
      await adminApi.delete(`/admin/services/${serviceId}`);
      await refreshSelection(selectedProfessional.professional.id);
      setSaveMessage('Usluga zostala wylaczona.');
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udalo sie wylaczyc uslugi.');
    } finally {
      setActionLoading(false);
    }
  }

  function addScheduleRow() {
    updateSelectedProfessional((prev) => ({
      ...prev,
      availability: [...prev.availability, createScheduleRow()],
    }));
  }

  function updateScheduleRow(rowKey, field, value) {
    updateSelectedProfessional((prev) => ({
      ...prev,
      availability: prev.availability.map((slot) => {
        const key = slot.id || slot.local_id;
        return key === rowKey ? { ...slot, [field]: value } : slot;
      }),
    }));
  }

  function removeScheduleRow(rowKey) {
    updateSelectedProfessional((prev) => ({
      ...prev,
      availability: prev.availability.filter((slot) => (slot.id || slot.local_id) !== rowKey),
    }));
  }

  async function handleSaveSchedule() {
    if (!selectedProfessional?.professional) return;

    const payload = selectedProfessional.availability.map((slot) => ({
      day_of_week: Number(slot.day_of_week),
      start_time: ensureTimeSeconds(slot.start_time),
      end_time: ensureTimeSeconds(slot.end_time),
      is_active: slot.is_active !== false,
    }));

    const invalidSlot = payload.find((slot) => slot.start_time >= slot.end_time);
    if (invalidSlot) {
      setError('Kazde okno grafiku musi miec poprawny zakres godzin.');
      return;
    }

    setActionLoading(true);
    setError('');
    setSaveMessage('');

    try {
      await adminApi.put(`/admin/professionals/${selectedProfessional.professional.id}/schedule`, { schedule: payload });
      await refreshSelection(selectedProfessional.professional.id);
      setSaveMessage('Grafik zostal zapisany.');
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udalo sie zapisac grafiku.');
    } finally {
      setActionLoading(false);
    }
  }

  function updateBlockDraft(field, value) {
    setBlockDraft((current) => ({ ...current, [field]: value }));
  }

  async function handleCreateBlock() {
    if (!selectedProfessional?.professional) return;

    setActionLoading(true);
    setError('');
    setSaveMessage('');

    try {
      await adminApi.post(`/admin/professionals/${selectedProfessional.professional.id}/blocks`, blockDraft);
      setBlockDraft(createBlockDraft());
      await refreshSelection(selectedProfessional.professional.id);
      setSaveMessage('Blokada zostala dodana.');
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udalo sie dodac blokady.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDeleteBlock(blockId) {
    setActionLoading(true);
    setError('');
    setSaveMessage('');

    try {
      await adminApi.delete(`/admin/blocks/${blockId}`);
      await refreshSelection(selectedProfessional.professional.id);
      setSaveMessage('Blokada zostala usunieta.');
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udalo sie usunac blokady.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRequestDecision(requestId, action) {
    setActionLoading(true);
    setError('');
    setSaveMessage('');

    try {
      await adminApi.put(`/admin/requests/${requestId}/${action}`);
      await refreshSelection(selectedProfessional.professional.id);
      setSaveMessage(action === 'accept' ? 'Prosba zostala zaakceptowana.' : 'Prosba zostala odrzucona.');
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udalo sie przetworzyc prosby.');
    } finally {
      setActionLoading(false);
    }
  }

  const selectedSummary = selectedProfessional?.summary;

  return (
    <div className="panel-page admin-page">
      <div className="panel-header admin-header">
        <div className="panel-header-copy">
          <span className="panel-eyebrow">ClientWay Admin</span>
          <h1 className="panel-title">Centrum operacyjne</h1>
          <p className="panel-subtitle">Admin moze uzupelnic konto, poprawic oferte, ustawic grafik i obsluzyc prosby bez pomocy uzytkownika.</p>
        </div>
        <div className="admin-header-actions">
          <span className="admin-badge"><ShieldCheck size={14} /> {admin?.email || 'superadmin'}</span>
          <button type="button" className="btn-secondary" onClick={logout}>Wyloguj</button>
        </div>
      </div>

      {error && <div className="section"><div className="alert alert-error">{error}</div></div>}
      {saveMessage && <div className="section"><div className="alert alert-success">{saveMessage}</div></div>}

      <section className="section admin-kpi-grid">
        <div className="admin-kpi-card"><strong>{dashboard?.stats?.professionals_total || 0}</strong><span>wszystkich kont</span></div>
        <div className="admin-kpi-card"><strong>{dashboard?.stats?.beta_total || 0}</strong><span>beta</span></div>
        <div className="admin-kpi-card"><strong>{dashboard?.stats?.active_total || 0}</strong><span>aktywnych</span></div>
        <div className="admin-kpi-card"><strong>{dashboard?.stats?.suspended_total || 0}</strong><span>zawieszonych</span></div>
        <div className="admin-kpi-card"><strong>{dashboard?.stats?.ready_to_activate_total || 0}</strong><span>gotowych do aktywacji</span></div>
        <div className="admin-kpi-card"><strong>{dashboard?.stats?.needs_attention_total || 0}</strong><span>wymagajacych uwagi</span></div>
      </section>

      <section className="section admin-layout">
        <div className="admin-panel-card">
          <div className="admin-inline-header">
            <strong>Konta wymagajace uwagi</strong>
            <span>{dashboard?.needs_attention?.length || 0}</span>
          </div>
          <div className="admin-list">
            {(dashboard?.needs_attention || []).map((item) => (
              <button key={`attention-${item.id}`} type="button" className="admin-list-item" onClick={() => setSelectedId(item.id)}>
                <strong>{item.name}</strong>
                <span>{item.email}</span>
                <span>{item.summary?.missing?.map((entry) => entry.label).join(' · ') || 'Wymaga sprawdzenia'}</span>
              </button>
            ))}
            {(!dashboard?.needs_attention || dashboard.needs_attention.length === 0) && !loading && (
              <p className="loading-text">Brak nowych kont wymagajacych szybkiej interwencji.</p>
            )}
          </div>
        </div>

        <div className="admin-panel-card">
          <div className="admin-inline-header">
            <strong>Ostatnie akcje admina</strong>
            <span>{dashboard?.recent_actions?.length || 0}</span>
          </div>
          <div className="admin-log-list">
            {(dashboard?.recent_actions || []).map((action) => (
              <div key={action.id} className="admin-log-item">
                <strong>{formatActionLabel(action.action)}</strong>
                <span>{action.admin_email} · {formatDate(action.created_at)}</span>
              </div>
            ))}
            {(!dashboard?.recent_actions || dashboard.recent_actions.length === 0) && !loading && (
              <p className="loading-text">Log administracyjny jest jeszcze pusty.</p>
            )}
          </div>
        </div>
      </section>

      <section className="section admin-layout">
        <div className="admin-panel-card">
          <form onSubmit={handleSearchSubmit} className="admin-search-form">
            <Search size={16} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Szukaj po nazwie, emailu, telefonie lub slugu" />
            <button type="submit" className="btn-secondary">Szukaj</button>
          </form>
          <div className="admin-list">
            {loading && <p className="loading-text">Ladowanie kont...</p>}
            {!loading && professionals.map((item) => (
              <button key={item.id} type="button" className={`admin-list-item ${selectedId === item.id ? 'is-active' : ''}`} onClick={() => setSelectedId(item.id)}>
                <strong>{item.name}</strong>
                <span>{item.email}</span>
                <span>{item.account_status} · {item.booking_slug || 'brak slugu'}</span>
                <span>{item.summary?.ready_for_activation ? 'Gotowe do aktywacji' : item.summary?.missing?.map((entry) => entry.label).join(' · ') || 'Brak diagnozy'}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="admin-panel-card admin-detail-card">
          {!selectedProfessional && !detailLoading && <p className="loading-text">Wybierz konto z listy po lewej.</p>}
          {detailLoading && <p className="loading-text">Ladowanie szczegolow...</p>}
          {selectedProfessional?.professional && (
            <div className="admin-detail-stack">
              <div>
                <span className="panel-eyebrow">Konto</span>
                <h2 className="admin-detail-title">{selectedProfessional.professional.name}</h2>
                <p className="panel-subtitle">{selectedProfessional.professional.email} · utworzone {formatDate(selectedProfessional.professional.created_at)}</p>
              </div>

              <div className="admin-summary-grid">
                <div className="admin-summary-card">
                  <strong>{selectedSummary?.ready_for_activation ? 'Gotowe' : 'W trakcie'}</strong>
                  <span>status operacyjny</span>
                </div>
                <div className="admin-summary-card">
                  <strong>{selectedSummary?.active_services_count || 0}</strong>
                  <span>aktywnych uslug</span>
                </div>
                <div className="admin-summary-card">
                  <strong>{selectedSummary?.schedule_windows_count || 0}</strong>
                  <span>okien grafiku</span>
                </div>
                <div className="admin-summary-card">
                  <strong>{selectedSummary?.pending_requests_count || 0}</strong>
                  <span>oczekujacych prosb</span>
                </div>
              </div>

              <div className="admin-checklist-card">
                <div className="admin-inline-header">
                  <strong>Checklist gotowosci</strong>
                  {selectedSummary?.ready_for_activation ? <span>konto spelnia warunki</span> : <span>wymaga uzupelnienia</span>}
                </div>
                {selectedSummary?.missing?.length > 0 ? (
                  <div className="admin-reason-list">
                    {selectedSummary.missing.map((item) => (
                      <div key={item.key} className="admin-reason-item"><AlertCircle size={14} /> {item.label}</div>
                    ))}
                  </div>
                ) : (
                  <div className="admin-reason-item admin-reason-item--success"><Sparkles size={14} /> Konto ma link, oferte i grafik.</div>
                )}
              </div>

              <div className="auth-form-grid">
                <div className="form-group">
                  <label>Status</label>
                  <select value={selectedProfessional.professional.account_status || 'beta'} onChange={(event) => updateSelectedProfessional((prev) => ({ ...prev, professional: { ...prev.professional, account_status: event.target.value } }))}>
                    <option value="demo">demo</option>
                    <option value="beta">beta</option>
                    <option value="active">active</option>
                    <option value="suspended">suspended</option>
                    <option value="archived">archived</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Slug rezerwacji</label>
                  <input value={selectedProfessional.professional.booking_slug || ''} readOnly />
                </div>
                <div className="form-group auth-form-full">
                  <label>Notatki admina</label>
                  <textarea rows="4" value={selectedProfessional.professional.admin_notes || ''} onChange={(event) => updateSelectedProfessional((prev) => ({ ...prev, professional: { ...prev.professional, admin_notes: event.target.value } }))} />
                </div>
              </div>

              <div className="onboarding-metrics">
                <div className="onboarding-metric"><strong>{selectedProfessional.services?.length || 0}</strong><span>uslug</span></div>
                <div className="onboarding-metric"><strong>{selectedProfessional.clients?.length || 0}</strong><span>ostatnich klientek</span></div>
                <div className="onboarding-metric"><strong>{selectedProfessional.requests?.length || 0}</strong><span>ostatnich prosb</span></div>
              </div>

              <div className="admin-inline-actions">
                <button type="button" className="btn-secondary" disabled={actionLoading} onClick={handleGenerateBookingSlug}>Generuj slug</button>
                <button type="button" className="btn-secondary" disabled={actionLoading || !selectedProfessional.professional.booking_slug} onClick={handleCopyBookingLink}><Copy size={14} /> Skopiuj link</button>
                <button type="button" className="btn-secondary" disabled={actionLoading} onClick={handleSendResetLink}>Wyslij reset hasla</button>
                <button type="button" className="btn-secondary" disabled={actionLoading || !selectedSummary?.ready_for_activation} onClick={handleActivateReady}>Aktywuj jesli gotowe</button>
                <button type="button" className="btn-primary" disabled={actionLoading} onClick={handleSaveProfile}>Zapisz zmiany</button>
              </div>

              <div className="admin-ops-grid">
                <div className="admin-editor-card">
                  <div className="admin-inline-header">
                    <strong>Oferta i dodatki</strong>
                    <span>{selectedProfessional.services?.length || 0} pozycji</span>
                  </div>

                  <div className="admin-create-service">
                    <div className="auth-form-grid">
                      <div className="form-group">
                        <label>Nazwa</label>
                        <input value={serviceDraft.name} onChange={(event) => updateServiceDraft('name', event.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>Czas</label>
                        <input type="number" min="5" step="5" value={serviceDraft.duration_minutes} onChange={(event) => updateServiceDraft('duration_minutes', event.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>Cena</label>
                        <input type="number" min="0" step="0.01" value={serviceDraft.price} onChange={(event) => updateServiceDraft('price', event.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>Promo cena</label>
                        <input type="number" min="0" step="0.01" value={serviceDraft.promo_price} onChange={(event) => updateServiceDraft('promo_price', event.target.value)} />
                      </div>
                      <div className="form-group auth-form-full">
                        <label>Opis</label>
                        <textarea rows="2" value={serviceDraft.description} onChange={(event) => updateServiceDraft('description', event.target.value)} />
                      </div>
                      <div className="form-group auth-form-full">
                        <label>Promo label</label>
                        <input value={serviceDraft.promo_label} onChange={(event) => updateServiceDraft('promo_label', event.target.value)} placeholder="np. Pierwsza wizyta" />
                      </div>
                    </div>

                    <div className="admin-subsection">
                      <div className="admin-inline-header">
                        <strong>Dodatki do nowej uslugi</strong>
                        <button type="button" className="btn-secondary" onClick={addServiceDraftAddon}><Plus size={14} /> Dodaj dodatek</button>
                      </div>
                      <div className="admin-addon-list">
                        {serviceDraft.addons.map((addon, index) => (
                          <div key={`draft-addon-${index}`} className="admin-addon-row">
                            <input value={addon.name} onChange={(event) => updateServiceDraftAddon(index, 'name', event.target.value)} placeholder="Nazwa dodatku" />
                            <input type="number" min="0" step="5" value={addon.duration_minutes} onChange={(event) => updateServiceDraftAddon(index, 'duration_minutes', event.target.value)} placeholder="min" />
                            <input type="number" min="0" step="0.01" value={addon.price} onChange={(event) => updateServiceDraftAddon(index, 'price', event.target.value)} placeholder="cena" />
                            <button type="button" className="btn-secondary" onClick={() => removeServiceDraftAddon(index)}><Trash2 size={14} /></button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button type="button" className="btn-primary" disabled={actionLoading} onClick={handleCreateService}>Dodaj usluge</button>
                  </div>

                  <div className="admin-service-list">
                    {selectedProfessional.services?.map((service) => (
                      <div key={service.id} className="admin-service-card">
                        <div className="admin-inline-header">
                          <strong>{service.name || 'Nowa usluga'}</strong>
                          <span>{service.is_active === false ? 'wylaczona' : 'aktywna'} · {formatMoney(service.effective_price || service.promo_price || service.price)}</span>
                        </div>
                        <div className="auth-form-grid">
                          <div className="form-group">
                            <label>Nazwa</label>
                            <input value={service.name || ''} onChange={(event) => updateExistingService(service.id, 'name', event.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Czas</label>
                            <input type="number" min="5" step="5" value={service.duration_minutes || ''} onChange={(event) => updateExistingService(service.id, 'duration_minutes', event.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Cena</label>
                            <input type="number" min="0" step="0.01" value={service.price || ''} onChange={(event) => updateExistingService(service.id, 'price', event.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Promo cena</label>
                            <input type="number" min="0" step="0.01" value={service.promo_price} onChange={(event) => updateExistingService(service.id, 'promo_price', event.target.value)} />
                          </div>
                          <div className="form-group auth-form-full">
                            <label>Opis</label>
                            <textarea rows="2" value={service.description || ''} onChange={(event) => updateExistingService(service.id, 'description', event.target.value)} />
                          </div>
                          <div className="form-group">
                            <label>Promo label</label>
                            <input value={service.promo_label || ''} onChange={(event) => updateExistingService(service.id, 'promo_label', event.target.value)} />
                          </div>
                          <label className="admin-checkbox-row">
                            <input type="checkbox" checked={service.is_active !== false} onChange={(event) => updateExistingService(service.id, 'is_active', event.target.checked)} />
                            <span>Aktywna usluga</span>
                          </label>
                        </div>

                        <div className="admin-subsection">
                          <div className="admin-inline-header">
                            <strong>Dodatki</strong>
                            <button type="button" className="btn-secondary" onClick={() => addExistingServiceAddon(service.id)}><Plus size={14} /> Dodaj</button>
                          </div>
                          <div className="admin-addon-list">
                            {(service.addons || []).map((addon, addonIndex) => (
                              <div key={`${service.id}-addon-${addon.id || addonIndex}`} className="admin-addon-row">
                                <input value={addon.name || ''} onChange={(event) => updateExistingServiceAddon(service.id, addonIndex, 'name', event.target.value)} placeholder="Nazwa dodatku" />
                                <input type="number" min="0" step="5" value={addon.duration_minutes || ''} onChange={(event) => updateExistingServiceAddon(service.id, addonIndex, 'duration_minutes', event.target.value)} placeholder="min" />
                                <input type="number" min="0" step="0.01" value={addon.price || ''} onChange={(event) => updateExistingServiceAddon(service.id, addonIndex, 'price', event.target.value)} placeholder="cena" />
                                <button type="button" className="btn-secondary" onClick={() => removeExistingServiceAddon(service.id, addonIndex)}><Trash2 size={14} /></button>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="admin-inline-actions">
                          <button type="button" className="btn-secondary" disabled={actionLoading} onClick={() => handleSaveService(service)}>Zapisz usluge</button>
                          <button type="button" className="btn-danger admin-compact-danger" disabled={actionLoading} onClick={() => handleDeactivateService(service.id)}>Wylacz</button>
                        </div>
                      </div>
                    ))}
                    {selectedProfessional.services?.length === 0 && <p className="loading-text">To konto nie ma jeszcze zadnej uslugi.</p>}
                  </div>
                </div>

                <div className="admin-editor-card">
                  <div className="admin-inline-header">
                    <strong>Grafik pracy</strong>
                    <span>{selectedProfessional.availability?.length || 0} okien</span>
                  </div>
                  <div className="admin-schedule-list">
                    {selectedProfessional.availability?.map((slot) => {
                      const slotKey = slot.id || slot.local_id;
                      return (
                        <div key={slotKey} className="admin-schedule-row">
                          <select value={slot.day_of_week} onChange={(event) => updateScheduleRow(slotKey, 'day_of_week', Number(event.target.value))}>
                            {DAY_LABELS.map((label, index) => <option key={label} value={index}>{label}</option>)}
                          </select>
                          <input type="time" value={normalizeTimeValue(slot.start_time)} onChange={(event) => updateScheduleRow(slotKey, 'start_time', ensureTimeSeconds(event.target.value))} />
                          <input type="time" value={normalizeTimeValue(slot.end_time)} onChange={(event) => updateScheduleRow(slotKey, 'end_time', ensureTimeSeconds(event.target.value))} />
                          <button type="button" className="btn-secondary" onClick={() => removeScheduleRow(slotKey)}><Trash2 size={14} /></button>
                        </div>
                      );
                    })}
                  </div>
                  <div className="admin-inline-actions">
                    <button type="button" className="btn-secondary" onClick={addScheduleRow}><Plus size={14} /> Dodaj okno</button>
                    <button type="button" className="btn-primary" disabled={actionLoading} onClick={handleSaveSchedule}>Zapisz grafik</button>
                  </div>

                  <div className="admin-subsection">
                    <div className="admin-inline-header">
                      <strong>Blokady i wyjatki</strong>
                      <span>{selectedProfessional.time_blocks?.length || 0} aktywnych</span>
                    </div>

                    <div className="auth-form-grid">
                      <div className="form-group">
                        <label>Powod</label>
                        <input value={blockDraft.reason} onChange={(event) => updateBlockDraft('reason', event.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>Typ</label>
                        <select value={blockDraft.block_type} onChange={(event) => updateBlockDraft('block_type', event.target.value)}>
                          <option value="break">break</option>
                          <option value="private">private</option>
                          <option value="vacation">vacation</option>
                          <option value="custom">custom</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Od</label>
                        <input type="datetime-local" value={blockDraft.blocked_from} onChange={(event) => updateBlockDraft('blocked_from', event.target.value)} />
                      </div>
                      <div className="form-group">
                        <label>Do</label>
                        <input type="datetime-local" value={blockDraft.blocked_until} onChange={(event) => updateBlockDraft('blocked_until', event.target.value)} />
                      </div>
                    </div>

                    <div className="admin-inline-actions">
                      <button type="button" className="btn-primary" disabled={actionLoading} onClick={handleCreateBlock}>Dodaj blokade</button>
                    </div>

                    <div className="admin-block-list">
                      {(selectedProfessional.time_blocks || []).map((block) => (
                        <div key={block.id} className="admin-block-row">
                          <div>
                            <strong>{block.reason}</strong>
                            <span>{block.block_type} · {formatDate(block.blocked_from)} - {formatDate(block.blocked_until)}</span>
                          </div>
                          <button type="button" className="btn-secondary" disabled={actionLoading} onClick={() => handleDeleteBlock(block.id)}><Trash2 size={14} /></button>
                        </div>
                      ))}
                      {selectedProfessional.time_blocks?.length === 0 && <p className="loading-text">Brak aktywnych blokad.</p>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="admin-detail-columns">
                <div className="admin-editor-card">
                  <div className="admin-inline-header">
                    <strong>Prosby rezerwacji</strong>
                    <span>{selectedProfessional.requests?.length || 0}</span>
                  </div>
                  <div className="admin-request-list">
                    {(selectedProfessional.requests || []).map((request) => (
                      <div key={request.id} className="admin-request-row">
                        <div>
                          <strong>{request.client_name || 'Klientka'}</strong>
                          <span>{request.service_name || 'Usluga'} · {formatDate(request.requested_slot_start)}</span>
                          <span>{request.client_phone} · status: {request.status}</span>
                        </div>
                        {request.status === 'pending' ? (
                          <div className="admin-inline-actions">
                            <button type="button" className="btn-secondary" disabled={actionLoading} onClick={() => handleRequestDecision(request.id, 'accept')}><CheckCircle2 size={14} /> Akceptuj</button>
                            <button type="button" className="btn-secondary" disabled={actionLoading} onClick={() => handleRequestDecision(request.id, 'reject')}><XCircle size={14} /> Odrzuc</button>
                          </div>
                        ) : (
                          <span className="admin-status-pill">{request.status}</span>
                        )}
                      </div>
                    ))}
                    {selectedProfessional.requests?.length === 0 && <p className="loading-text">Brak prosb do obslugi.</p>}
                  </div>
                </div>

                <div className="admin-editor-card">
                  <div className="admin-inline-header">
                    <strong>Ostatnie wizyty</strong>
                    <span>{selectedProfessional.appointments?.length || 0}</span>
                  </div>
                  <div className="admin-mini-list">
                    {(selectedProfessional.appointments || []).map((appointment) => (
                      <div key={appointment.id} className="admin-block-row">
                        <div>
                          <strong>{appointment.client_name || 'Klientka'}</strong>
                          <span>{appointment.service_name} · {formatDate(appointment.scheduled_at)}</span>
                        </div>
                        <span className="admin-status-pill">{appointment.status}</span>
                      </div>
                    ))}
                    {selectedProfessional.appointments?.length === 0 && <p className="loading-text">Brak ostatnich wizyt.</p>}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="section-title">Historia akcji admina</h3>
                <div className="admin-log-list">
                  {selectedProfessional.admin_actions?.map((action) => (
                    <div key={action.id} className="admin-log-item">
                      <strong>{formatActionLabel(action.action)}</strong>
                      <span>{action.admin_email} · {formatDate(action.created_at)}</span>
                    </div>
                  ))}
                  {(!selectedProfessional.admin_actions || selectedProfessional.admin_actions.length === 0) && (
                    <p className="loading-text">Brak zapisanych akcji dla tego konta.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}